/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../logger';
import { getGlobalPrebidObj } from '../../util';
import { SiteConfig } from '../config';
import { Placement } from './index';
import { DailyMotionVideoPlacement } from './video/player/dailymotion';
import { DigitekaVideoPlacement } from './video/player/digiteka';
import { JwPlayerVideoPlacement } from './video/player/jwplayer';
import { PrebidVideoPlacement } from './video/prebid';

import type { CampaignData } from '../../types/index';
import type { PrebidAdUnit } from '../../types/prebid';

const logger = Logger.getInstance();

class PlacementManager {
  private static instance: PlacementManager;
  private placementFactory: (
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) => Placement | undefined;
  private placementCache: Map<string, Placement>;

  private constructor(
    placementFactory: (
      adUnit: PrebidAdUnit,
      adUnitTransformer: (adUnitCode: string) => string,
      campaignData: CampaignData[],
      siteConfig: SiteConfig,
    ) => Placement | undefined,
  ) {
    this.placementFactory = placementFactory;
    this.placementCache = new Map<string, Placement>();
  }

  public static getInstance(
    placementFactory: (
      adUnit: PrebidAdUnit,
      adUnitTransformer: (adUnitCode: string) => string,
      campaignData: CampaignData[],
      siteConfig: SiteConfig,
    ) => Placement | undefined,
  ): PlacementManager {
    if (!PlacementManager.instance) {
      PlacementManager.instance = new PlacementManager(placementFactory);
    }
    return PlacementManager.instance;
  }

  /**
   * Determines which video placement implementation to use based on what's available on the page.
   * Priority:
   * 1. Digiteka player (if window.DtkPlayer or window.dtkPlayer exists)
   * 2. Dailymotion player (if window.dailymotion exists)
   * 3. Prebid video providers (jwplayer, videojs, adplayerpro)
   * 4. JW Player embedded directly on the page (no Prebid video module)
   * 5. Default to Prebid video placement
   *
   * The Prebid video module is checked before the direct JW Player integration
   * so that publishers running `jwplayerVideoProvider` keep the existing
   * behaviour: where that module is installed it owns the player's ad events,
   * and `PrebidVideoPlacement` is the correct consumer of them.
   */
  private createVideoPlacement(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ): Placement {
    if (
      typeof window !== 'undefined' &&
      ((window as any).DtkPlayer !== undefined || (window as any).dtkPlayer !== undefined)
    ) {
      return new DigitekaVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
    }

    if (typeof window !== 'undefined' && (window as any).dailymotion !== undefined) {
      return new DailyMotionVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
    }

    const prebidObj = getGlobalPrebidObj();
    if (prebidObj?.installedModules) {
      const videoModules = [
        'jwplayerVideoProvider',
        'videojsVideoProvider',
        'adplayerproVideoProvider',
      ];
      const hasVideoModule = videoModules.some((module) =>
        prebidObj.installedModules.includes(module),
      );

      if (hasVideoModule) {
        return new PrebidVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
      }
    }

    if (typeof window !== 'undefined' && typeof (window as any).jwplayer === 'function') {
      return new JwPlayerVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
    }

    logger.info('No specific video player detected, defaulting to PrebidVideoPlacement');
    return new PrebidVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
  }

  public getPlacement(
    adUnit: PrebidAdUnit,
    campaignData: CampaignData[] = [],
    adUnitTransformer: (adUnitCode: string) => string = (adUnitCode) => adUnitCode,
    siteConfig: SiteConfig,
  ): Placement | undefined {
    // Check cache first
    const adUnitCode = adUnitTransformer(adUnit.code);
    const cachedPlacement = this.placementCache.get(adUnitCode);
    if (cachedPlacement) {
      logger.info(`Reusing existing placement for adUnit: ${adUnitCode}`);
      return cachedPlacement;
    }

    logger.info(`Creating new placement for adUnit: ${adUnitCode}`);
    let placement: Placement | undefined;

    // Check if mediaTypes only has video property
    const mediaTypes = adUnit.mediaTypes;
    const hasVideo = !!mediaTypes.video;
    const hasBanner = !!mediaTypes.banner;
    const hasOtherMediaTypes =
      Object.keys(mediaTypes).some((key) => key !== 'video' && key !== 'banner') || hasBanner;

    if (hasVideo && !hasOtherMediaTypes) {
      // Only video mediaType present, use video placement factory
      placement = this.createVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
    } else {
      // Use the factory for all other cases
      try {
        placement = this.placementFactory(adUnit, adUnitTransformer, campaignData, siteConfig);
      } catch (error) {
        logger.error(`Error creating placement for adUnit: ${adUnitCode}: ${error}`);
        return;
      }
    }

    // Cache and return
    this.placementCache.set(adUnitCode, placement!);
    return placement;
  }
}

export { PlacementManager };
